/**
 * A scrapbook note (the owner, 2026-10-01, from docs/specs/D19-SCRAPBOOK-NOTES-REFERENCE.html): handwritten words on a
 * piece of hand-torn paper, held by washi tape or a pin. In the glass look the edge is torn by an SVG filter (defined
 * once in the shell, #rtcom-torn) and the words are in Kalam; in the classic look it is a plain quiet box with the same
 * words, so ADR-0011's look is unchanged. A note carries real words only — an office note, a rule, an empty list's
 * message — never invented content, and it never covers a figure.
 */
import type { ReactNode } from 'react';
import { cx } from './primitives';

export type Paper = 'yellow' | 'cream' | 'pink' | 'blue' | 'mint' | 'kraft' | 'lined';

export function ScrapNote({ title, children, paper = 'yellow', hold = 'tape', tilt = -2, className, lang }: {
  title?: ReactNode;
  children?: ReactNode;
  paper?: Paper;
  /** What holds it to the page. */
  hold?: 'tape' | 'pin' | 'none';
  /** Degrees; the note straightens on hover. */
  tilt?: number;
  className?: string;
  lang?: string;
}) {
  return (
    <div lang={lang} role="note" style={{ ['--tilt' as string]: `${tilt}deg` }}
      className={cx('scrap-note relative inline-block max-w-full rounded-[var(--radius-control)] border border-line bg-surface-sunken px-4 py-3 text-body text-ink', `scrap-${paper}`, className)}>
      {hold === 'tape' ? <i aria-hidden className="scrap-tape" /> : hold === 'pin' ? <i aria-hidden className="scrap-pin" /> : null}
      {title ? <p className="scrap-title font-semibold">{title}</p> : null}
      {children ? <div className="scrap-body">{children}</div> : null}
    </div>
  );
}

/** The two filters the torn edge needs; the shell renders this once. */
export function ScrapFilters() {
  return (
    <svg width="0" height="0" aria-hidden className="absolute" focusable="false">
      <filter id="rtcom-torn" x="-5%" y="-5%" width="110%" height="110%">
        <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="4" seed="7" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="11" xChannelSelector="R" yChannelSelector="G" />
      </filter>
      <filter id="rtcom-tape" x="-10%" y="-20%" width="120%" height="140%">
        <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="3" result="n" />
        <feDisplacementMap in="SourceGraphic" in2="n" scale="5" />
      </filter>
    </svg>
  );
}
